// src/services/testHistoryApi.js
import { supabase } from "../../../../Backend/supabaseClient";
import { getUploadStatusByTestId, getAudioFromDB } from "../../../Utility/audioService";
import { getCurrentUserId } from "../../../../Backend/Authentication/UserStore";
import { MY_BACKEND_URL } from "../../../../Backend/supabaseClient";

const BACKEND_BASE_URL = MY_BACKEND_URL;

async function hydrateQuestionsAndSolutions(skeleton) {
    // collect ids
    const ids = Array.from(
        new Set(
            skeleton
                .map((q) => q.question_id)
                .filter(Boolean)
        )
    );

    if (ids.length === 0) return skeleton;
    const { data: { session } } = await supabase.auth.getSession();

    if (!session || !session.user) {
        throw new Error("You must be logged in to join a meet");
    }

    const accessToken = session.access_token;

    const res = await fetch(`${BACKEND_BASE_URL}/api/question-meta-batch`, {
        method: "POST",
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ questionIds: ids }),
    });

    if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`Failed to fetch question meta: ${res.status} ${t}`);
    }

    const { items } = await res.json();
    if (!items) return skeleton;

    // merge
    for (const q of skeleton) {
        if (!q.question_id) continue;
        const meta = items[q.question_id];
        if (!meta) continue;

        q.question_text = meta.question_text ?? q.question_text;
        q.img_src = meta.img_src ?? q.img_src;
        q.correct_solution = meta.correct_solution ?? q.correct_solution;
    }

    return skeleton;
}


function createQuestionSkeleton(totalQuestions) {
    return Array.from({ length: totalQuestions }, (_, i) => ({
        index: i + 1,
        question_id: null,
        local_url: null,

        answer_text: null,
        verdict: null,
        comments: null,
        ai_analysis: null,

        question_text: null,
        img_src: null,
        correct_solution: null,
        status:null
    }));
}

async function mergeLocalIntoSkeleton({ testId, skeleton }) {
    const localRows = await getUploadStatusByTestId(testId);
    // localRows: [{id(question_id), index, testId, ...}, ...]

    for (const row of localRows) {
        const idx = row.index; // 1-based
        if (!idx || idx < 1 || idx > skeleton.length) continue;

        const slot = skeleton[idx - 1];
        if (row.id) slot.question_id = row.id;
    }

    return skeleton;
}

function mergeServerResultsIntoSkeleton({ skeleton, serverResults }) {
    // serverResults: [{ index, question_id, verdict, comments, ai_analysis, answer_text }, ...]

    for (const row of serverResults || []) {
        const idx = row.index;
        if (!idx || idx < 1 || idx > skeleton.length) continue;

        const slot = skeleton[idx - 1];

        // overwrite question_id from server (you asked for overwrite)
        if (row.question_id) slot.question_id = row.question_id;

        // fill result fields (may be null if processing incomplete)
        slot.verdict = row.verdict ?? slot.verdict;
        slot.comments = row.comments ?? slot.comments;
        slot.ai_analysis = row.ai_analysis ?? slot.ai_analysis;
        slot.answer_text = row.answer_text ?? slot.answer_text;
        slot.status = row.status ?? slot.status;
    }

    return skeleton;
}




export async function fetchTestHistory(testId) {
    const { data, error } = await supabase
        .from("test_history")
        .select("id, user_id, title, score, total_questions, created_at")
        .eq("id", testId)
        .single();

    if (error) throw new Error(error.message);
    return data;
}


export async function buildQuestionAttempts({ testId, totalQuestions }) {
    // 1) skeleton
    let userId = getCurrentUserId();
    let skeleton = createQuestionSkeleton(totalQuestions);

    // 2) merge local
    skeleton = await mergeLocalIntoSkeleton({ testId, skeleton });

    // 3) fetch server results (simple direct supabase query)
    const { data: serverResults, error } = await supabase
        .from("test_results")
        .select("index, question_id, verdict,  answer_text, comments, ai_analysis, status")
        .eq("test_id", testId)
        .eq("user_id", userId)
        .order("index", { ascending: true });

    if (error) throw new Error(error.message);

    // 4) merge server into skeleton
    skeleton = mergeServerResultsIntoSkeleton({ skeleton, serverResults });

    for (const q of skeleton) {
        if (q.question_id) q.local_url = (await getAudioFromDB(q.question_id)) || null;
    }
    skeleton = await hydrateQuestionsAndSolutions(skeleton);

    return skeleton;
}


export async function fetchQuestionResultIfNeeded({
  testId,
  userId,
  attempt, // the selected attempt object
}) {
  if (!attempt?.index) return null;

  const needs =
    attempt.status != "success" ||
    attempt.verdict == null ||
    attempt.answer_text == null ||
    attempt.verdict == null ||
    attempt.comments == null ||
    attempt.ai_analysis == null;

  // Already loaded
  if (!needs) return null;

  // We need a question_id to fetch test_results
  // (If question_id is still null, nothing to load yet)
  if (!attempt.question_id) return null;

  const { data, error } = await supabase
    .from("test_results")
    .select("index, question_id, verdict, answer_text, comments, ai_analysis, status")
    .eq("test_id", testId)
    .eq("user_id", userId)
    .eq("question_id", attempt.question_id)
    .maybeSingle(); // returns null if no row

  if (error) throw new Error(error.message);
  if (!data) return null;

  return data; // return fetched row to merge into state
}
